import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';

import { TimeRangeName } from '../helpers/ShowMetrics';

type Props = {
    range: TimeRangeName;
    setRange: React.Dispatch<React.SetStateAction<TimeRangeName>>;
    startDate: Moment;
    setStartDate: React.Dispatch<React.SetStateAction<Moment>>;
    endDate: Moment;
    setEndDate: React.Dispatch<React.SetStateAction<Moment>>;
    displayRanges?: TimeRangeName[];
    containerClasses?: string;
};

const options = {
    today: 'Today',
    yesterday: 'Yesterday',
    'this-week': 'This week',
    'this-month': 'This month',
    'last-week': 'Last week',
    'last-month': 'Last month',
    'date-range': 'Date range',
    'all-time': 'All time',
};

export function getNameByRange(range: TimeRangeName) {
    return options[range];
}

export default function DateSelectRange({
    range,
    setRange,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    displayRanges = [],
    containerClasses = '',
}: Props) {
    const [startDatePicker, setStartDatePicker] = useState(moment(startDate).toDate());
    const [endDatePicker, setEndDatePicker] = useState(moment(endDate).toDate());

    function updateDates(newRange: TimeRangeName) {
        setRange(newRange);
        let start = moment();
        let end = moment();

        switch (newRange) {
            case 'today':
                start = start.startOf('day');
                end = end.endOf('day');
                break;
            case 'yesterday':
                start = start.subtract(1, 'days').startOf('day');
                end = end.subtract(1, 'days').endOf('day');
                break;
            case 'this-week':
                start = start.startOf('isoWeek');
                end = end.endOf('isoWeek');
                break;
            case 'this-month':
                start = start.startOf('month');
                end = end.endOf('month');
                break;
            case 'last-week':
                start = start.subtract(1, 'weeks').startOf('isoWeek');
                end = end.subtract(1, 'weeks').endOf('isoWeek');
                break;
            case 'last-month':
                start = start.subtract(1, 'months').startOf('month');
                end = end.subtract(1, 'months').endOf('month');
                break;
            case 'all-time':
                start = moment(null);
                end = moment(null);
                break;
            case 'date-range':
            default:
                return;
        }

        setStartDate(start);
        setEndDate(end);
    }

    useEffect(() => {
        setStartDatePicker(startDate?.isValid() ? startDate.toDate() : null);
        setEndDatePicker(endDate?.isValid() ? endDate.toDate() : null);
    }, [startDate, endDate]);

    const handleDateChange = (dates: [Date, Date]) => {
        setStartDate(dates[0] ? moment(dates[0]) : null);
        setEndDate(dates[1] ? moment(dates[1]) : null);
    };

    return (
        <div className={containerClasses}>
            <div className="dl m-b-10">
                <select
                    className="custom-select border-0 text-muted"
                    value={range ?? ''}
                    onChange={(event) => updateDates(event.target.value as TimeRangeName)}
                >
                    {displayRanges.length > 0
                        ? displayRanges.map((element) => (
                              <option key={element} value={element}>
                                  {options[element]}
                              </option>
                          ))
                        : Object.entries(options).map(([value, label]) => (
                              <option key={value} value={value}>
                                  {label}
                              </option>
                          ))}
                </select>
            </div>
            {range === 'date-range' && (
                <DatePicker
                    selectsRange
                    startDate={startDatePicker}
                    endDate={endDatePicker}
                    onChange={handleDateChange}
                    dateFormat="dd/MM/yyyy"
                    className="form-control"
                />
            )}
        </div>
    );
}
